import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { USER_MGMT } from "../constants/api";

// Export USER_MGMT for use in apps
export { USER_MGMT };
import { attemptTokenRefresh } from "./refreshToken/refreshTokenManager";
import {
  shouldAttemptRefresh,
  handleLogout,
} from "./authUtils/authErrorHandler";

/**
 * - Automatic token refresh on 401/403/FETCH_ERROR errors
 * - Prevents concurrent refresh attempts (singleton pattern)
 * - Retry limit protection (prevents infinite loops)
 * - Timeout handling with proper request cancellation (AbortSignal)
 * - Proper error handling and logout
 *
 * @param baseUrl - The base URL for the API service
 * @param logoutAction - The logout action from the app's auth slice (optional, for backward compatibility)
 * @returns BaseQueryFn with automatic token refresh capability
 */
export const createBaseQueryWithReauth = (
  baseUrl: string,
  logoutAction?: any
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    credentials: "include", // Always send HTTP-only cookies
  });

  // Separate base query for refresh token endpoint
  const refreshBaseQuery = fetchBaseQuery({
    baseUrl: USER_MGMT.href,
    credentials: "include",
  });

  return async (args, api, extraOptions) => {
    // Attempt the original request
    const result = await baseQuery(args, api, extraOptions);

    // Check if error requires token refresh
    const errorStatus = result.error?.status;
    const shouldRefresh = result.error && shouldAttemptRefresh(errorStatus);

    if (shouldRefresh) {
      // Attempt to refresh the access token (with singleton protection & timeout)
      const refreshResult = await attemptTokenRefresh(refreshBaseQuery, api);

      // Check if refresh was successful
      if (refreshResult.data?.success) {
        // Retry with clean args (no extraOptions to avoid cancelled signal propagation)
        const retryArgs = typeof args === 'string' ? args : { ...args };
        const retryResult = await baseQuery(retryArgs, api, {});
        return retryResult;
      } else {
        if ((refreshResult.error as { status?: string })?.status === 'CANCELLED') {
          return result;
        }
        // Refresh failed or timed out - logout user
        if (logoutAction) {
          handleLogout(api, logoutAction);
        }
        return result; // Return original error
      }
    }

    // For other errors, pass through without refresh
    return result;
  };
};

// Pre-configured base queries for different services
export const baseQueryWithReauth = createBaseQueryWithReauth(USER_MGMT.href);
